import React, { useEffect, useMemo, useState } from 'react';
import {
  getComprobantes, getComprobante, getComprobantesMeta, getPUC,
  crearComprobante, editarComprobante, contabilizarComp, anularComprobante, borrarComprobante,
} from '../services/api';
import FiltroPanel from '../components/FiltroPanel';

const fmtCOP = n => '$' + Number(n || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hoyStr = () => new Date().toISOString().slice(0, 10);
const BG_ESTADO    = { borrador: '#fef3c7', contabilizado: '#dcfce7', anulado: '#fee2e2' };
const TEXTO_ESTADO = { borrador: '#92400e', contabilizado: '#166534', anulado: '#991b1b' };

export default function Comprobantes() {
  const [lista, setLista]       = useState([]);
  const [tipos, setTipos]       = useState([]);
  const [cuentas, setCuentas]   = useState([]);
  const [filtro, setFiltro]     = useState({ tipo: '', estado: '' });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [editor, setEditor]     = useState(null);  // {modo: 'crear'|'editar'|'ver', id?}

  const cargarLista = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtro.tipo)   params.tipo   = filtro.tipo;
      if (filtro.estado) params.estado = filtro.estado;
      const res = await getComprobantes(params);
      setLista(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [meta, puc] = await Promise.all([getComprobantesMeta(), getPUC({ activa: true })]);
        setTipos(meta.data.tipos);
        setCuentas(puc.data.filter(c => c.acepta_movimiento));
      } catch (err) { setError('Error al cargar catálogos'); }
    })();
  }, []);

  useEffect(() => { cargarLista(); }, [filtro.tipo, filtro.estado]);

  const onContabilizar = async (id) => {
    try { await contabilizarComp(id); cargarLista(); }
    catch (err) { alert(err.response?.data?.error || 'Error al contabilizar'); }
  };
  const onAnular = async (id) => {
    if (!window.confirm('¿Anular este comprobante? Quedará marcado pero conservará su histórico.')) return;
    try { await anularComprobante(id); cargarLista(); }
    catch (err) { alert(err.response?.data?.error || 'Error al anular'); }
  };
  const onBorrar = async (id) => {
    if (!window.confirm('¿Eliminar este borrador?')) return;
    try { await borrarComprobante(id); cargarLista(); }
    catch (err) { alert(err.response?.data?.error || 'Error al eliminar'); }
  };

  const colorEstado = e => ({
    borrador: '#fef3c7', contabilizado: '#dcfce7', anulado: '#fee2e2',
  })[e] || '#f1f5f9';

  return (
    <div style={S.container}>
      <div style={S.headerRow}>
        <div>
          <h1 style={S.title}>Comprobantes contables</h1>
          <p style={S.sub}>Recibos de caja, egresos, asientos generales — partida doble</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <FiltroPanel
            fields={[
              { tipo: 'select', label: 'Tipo', value: filtro.tipo, onChange: v => setFiltro(f => ({ ...f, tipo: v })), width: 220, options: [{ value: '', label: 'Todos' }, ...tipos.map(t => ({ value: t.codigo, label: `${t.codigo} — ${t.nombre}` }))] },
              { tipo: 'select', label: 'Estado', value: filtro.estado, onChange: v => setFiltro(f => ({ ...f, estado: v })), options: [{ value: '', label: 'Todos' }, { value: 'borrador', label: 'Borrador' }, { value: 'contabilizado', label: 'Contabilizado' }, { value: 'anulado', label: 'Anulado' }] },
            ]}
            onClear={() => setFiltro({ tipo: '', estado: '' })}
          />
          <button style={S.btnPri} onClick={() => setEditor({ modo: 'crear' })}>+ Nuevo comprobante</button>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '0 0 12px' }}>
        <span style={S.count}>{lista.length} comprobantes</span>
      </div>

      {error && <div style={S.error}>{error}</div>}

      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Número</th>
            <th style={S.th}>Tipo</th>
            <th style={S.th}>Fecha</th>
            <th style={S.th}>Descripción</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
            <th style={S.th}>Estado</th>
            <th style={S.th}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading
            ? <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', padding: 40 }}>Cargando…</td></tr>
            : lista.length === 0
              ? <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', padding: 40, color: '#94a3b8' }}>Sin comprobantes</td></tr>
              : lista.map(c => (
                <tr key={c.id}>
                  <td style={{ ...S.td, fontFamily: 'monospace' }}>{c.numero}</td>
                  <td style={S.td}>{c.tipo}</td>
                  <td style={S.td}>{c.fecha}</td>
                  <td style={S.td}>{c.descripcion || '—'}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace' }}>{fmtCOP(c.total_debito)}</td>
                  <td style={S.td}>
                    <span style={{ ...S.badge, background: colorEstado(c.estado), color: TEXTO_ESTADO[c.estado] || '#334155' }}>{c.estado}</span>
                  </td>
                  <td style={S.tdAcc}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                      <button style={S.btnView} onClick={() => setEditor({ modo: 'ver', id: c.id })}>Ver</button>
                      {c.estado === 'borrador' && (
                        <>
                          <button style={S.iconBtn} title="Editar" onClick={() => setEditor({ modo: 'editar', id: c.id })}>✏️</button>
                          <button style={S.btnOk}   onClick={() => onContabilizar(c.id)}>Contabilizar</button>
                          <button style={{ ...S.iconBtn, borderColor: '#7f1d1d' }} title="Eliminar" onClick={() => onBorrar(c.id)}>🚫</button>
                        </>
                      )}
                      {c.estado === 'contabilizado' && (
                        <button style={S.btnDel} onClick={() => onAnular(c.id)}>Anular</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
        </tbody>
      </table>

      {editor && (
        <ModalComprobante
          modo={editor.modo}
          id={editor.id}
          tipos={tipos}
          cuentas={cuentas}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); cargarLista(); }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
function ModalComprobante({ modo, id, tipos, cuentas, onClose, onSaved }) {
  const [cabecera, setCabecera] = useState({
    tipo: 'CG', fecha: hoyStr(), descripcion: '', referencia: '',
  });
  const [lineas, setLineas] = useState([
    { cuenta_codigo: '', debito: 0, credito: 0, descripcion: '' },
    { cuenta_codigo: '', debito: 0, credito: 0, descripcion: '' },
  ]);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const readOnly = modo === 'ver';

  // Cargar cuando es ver/editar
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data } = await getComprobante(id);
        setOriginal(data);
        setCabecera({
          tipo: data.tipo, fecha: data.fecha, descripcion: data.descripcion || '',
          referencia: data.referencia || '',
        });
        setLineas(data.asientos.map(a => ({
          cuenta_codigo: a.cuenta_codigo,
          debito:        Number(a.debito),
          credito:       Number(a.credito),
          descripcion:   a.descripcion || '',
        })));
      } catch (err) { setError('Error al cargar comprobante'); }
    })();
  }, [id]);

  const totales = useMemo(() => {
    const d = lineas.reduce((s, l) => s + Number(l.debito  || 0), 0);
    const c = lineas.reduce((s, l) => s + Number(l.credito || 0), 0);
    return { d: +d.toFixed(2), c: +c.toFixed(2), dif: +(d - c).toFixed(2) };
  }, [lineas]);

  const balanceado = totales.d > 0 && totales.dif === 0;

  const setLinea = (i, patch) => {
    setLineas(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  };
  const addLinea = () => setLineas(prev => [...prev, { cuenta_codigo: '', debito: 0, credito: 0, descripcion: '' }]);
  const delLinea = (i) => setLineas(prev => prev.length <= 2 ? prev : prev.filter((_, idx) => idx !== i));

  const guardar = async () => {
    setError(''); setSaving(true);
    try {
      const payload = {
        ...cabecera,
        asientos: lineas.map(l => ({
          cuenta_codigo: l.cuenta_codigo,
          debito:        Number(l.debito  || 0),
          credito:       Number(l.credito || 0),
          descripcion:   l.descripcion,
        })),
      };
      if (modo === 'crear') await crearComprobante(payload);
      else                  await editarComprobante(id, payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <div style={S.modalBg} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.modalHead}>
          <h3 style={{ margin: 0, color: '#0f172a' }}>
            {modo === 'crear' ? 'Nuevo comprobante' : modo === 'editar' ? `Editar ${original?.numero || '…'}` : `Comprobante ${original?.numero || '…'}`}
          </h3>
          {original?.estado && (
            <span style={{ ...S.badge, background: BG_ESTADO[original.estado], color: TEXTO_ESTADO[original.estado] || '#334155' }}>
              {original.estado}
            </span>
          )}
          <button onClick={onClose} style={S.btnClose}>✕</button>
        </div>

        {error && <div style={S.error}>{error}</div>}

        {/* Cabecera */}
        <div style={S.row3}>
          <div>
            <label style={S.label}>Tipo</label>
            <select disabled={readOnly || modo === 'editar'} value={cabecera.tipo}
                    onChange={e => setCabecera(c => ({ ...c, tipo: e.target.value }))}
                    style={S.input}>
              {tipos.map(t => <option key={t.codigo} value={t.codigo}>{t.codigo} — {t.nombre}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>Fecha</label>
            <input disabled={readOnly} type="date" value={cabecera.fecha}
                   onChange={e => setCabecera(c => ({ ...c, fecha: e.target.value }))}
                   style={S.input} />
          </div>
          <div>
            <label style={S.label}>Referencia</label>
            <input disabled={readOnly} value={cabecera.referencia}
                   onChange={e => setCabecera(c => ({ ...c, referencia: e.target.value }))}
                   placeholder="ej. FAC-0001" style={S.input} />
          </div>
        </div>
        <div>
          <label style={S.label}>Descripción</label>
          <input disabled={readOnly} value={cabecera.descripcion}
                 onChange={e => setCabecera(c => ({ ...c, descripcion: e.target.value }))}
                 placeholder="Concepto del comprobante" style={S.input} />
        </div>

        {/* Líneas */}
        <h4 style={{ margin: '20px 0 8px', color: '#0f172a', fontSize: 14 }}>Asientos</h4>
        <table style={S.tableInner}>
          <thead>
            <tr>
              <th style={S.thInner}>Cuenta</th>
              <th style={S.thInner}>Detalle</th>
              <th style={{ ...S.thInner, textAlign: 'right', width: 130 }}>Débito</th>
              <th style={{ ...S.thInner, textAlign: 'right', width: 130 }}>Crédito</th>
              {!readOnly && <th style={{ ...S.thInner, width: 30 }}></th>}
            </tr>
          </thead>
          <tbody>
            {lineas.map((l, i) => (
              <tr key={i}>
                <td style={S.tdInner}>
                  <select disabled={readOnly} value={l.cuenta_codigo}
                          onChange={e => setLinea(i, { cuenta_codigo: e.target.value })}
                          style={{ ...S.input, fontSize: 12 }}>
                    <option value="">— Selecciona cuenta —</option>
                    {cuentas.map(c => (
                      <option key={c.codigo} value={c.codigo}>{c.codigo} — {c.nombre}</option>
                    ))}
                  </select>
                </td>
                <td style={S.tdInner}>
                  <input disabled={readOnly} value={l.descripcion}
                         onChange={e => setLinea(i, { descripcion: e.target.value })}
                         style={{ ...S.input, fontSize: 12 }} />
                </td>
                <td style={S.tdInner}>
                  <input disabled={readOnly} type="number" min="0" step="0.01"
                         value={l.debito || ''}
                         onChange={e => setLinea(i, { debito: e.target.value, credito: 0 })}
                         style={{ ...S.input, fontSize: 12, textAlign: 'right' }} />
                </td>
                <td style={S.tdInner}>
                  <input disabled={readOnly} type="number" min="0" step="0.01"
                         value={l.credito || ''}
                         onChange={e => setLinea(i, { credito: e.target.value, debito: 0 })}
                         style={{ ...S.input, fontSize: 12, textAlign: 'right' }} />
                </td>
                {!readOnly && (
                  <td style={S.tdInner}>
                    <button onClick={() => delLinea(i)} style={S.btnX} title="Eliminar línea">×</button>
                  </td>
                )}
              </tr>
            ))}
            <tr style={{ background: '#f8fafc', fontWeight: 600 }}>
              <td style={S.tdInner} colSpan={2}>Totales</td>
              <td style={{ ...S.tdInner, textAlign: 'right', fontFamily: 'monospace' }}>{fmtCOP(totales.d)}</td>
              <td style={{ ...S.tdInner, textAlign: 'right', fontFamily: 'monospace' }}>{fmtCOP(totales.c)}</td>
              {!readOnly && <td style={S.tdInner}></td>}
            </tr>
          </tbody>
        </table>

        {!readOnly && <button onClick={addLinea} style={S.btnAdd}>+ Agregar línea</button>}

        <div style={{ ...S.balance, color: balanceado ? '#15803d' : '#b91c1c' }}>
          {totales.d === 0 && totales.c === 0
            ? 'Ingresa montos en débitos y créditos'
            : balanceado
              ? '✓ Partida doble balanceada'
              : `Diferencia ${fmtCOP(Math.abs(totales.dif))} — débitos ${totales.d > totales.c ? 'mayor' : 'menor'} que créditos`}
        </div>

        {!readOnly && (
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button onClick={onClose} style={S.btnSec} disabled={saving}>Cancelar</button>
            <button onClick={guardar} style={S.btnPri} disabled={saving || (lineas.some(l => !l.cuenta_codigo))}>
              {saving ? 'Guardando…' : 'Guardar borrador'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
const S = {
  container: { padding: 24, maxWidth: 1300, margin: '0 auto' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18, flexWrap: 'wrap', gap: 12 },
  title:     { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  sub:       { color: '#64748b', margin: '4px 0 0', fontSize: 13 },
  filtros:   { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' },
  select:    { padding: '0 10px', height: 38, boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff' },
  count:     { color: '#64748b', fontSize: 13, marginLeft: 'auto' },
  error:     { background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  table:     { width: '100%', borderCollapse: 'collapse', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: 8, overflow: 'hidden' },
  th:        { background: '#f1f5f9', padding: '10px 12px', textAlign: 'left', fontSize: 12, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid #e2e8f0' },
  td:        { padding: '4px 12px', fontSize: 13, color: '#1e293b', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  tdAcc:     { padding: '6px 12px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' },
  badge:     { padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: 'capitalize' },
  btnPri:    { padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  btnSec:    { flex: 1, padding: '10px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  btnView:   { padding: '4px 10px', background: '#64748b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 },
  btnEdit:   { padding: '4px 10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 },
  btnOk:     { padding: '4px 10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 },
  btnDel:    { padding: '4px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 },
  iconBtn:   { background: '#1e293b', border: '1px solid #334155', borderRadius: 6, width: 26, height: 26, cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 },
  btnAdd:    { marginTop: 8, padding: '6px 14px', background: '#dbeafe', color: '#1d4ed8', border: '1px dashed #93c5fd', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  btnX:      { background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '2px 6px' },
  btnClose:  { background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b', marginLeft: 'auto' },

  modalBg:   { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20, overflowY: 'auto' },
  modal:     { background: '#fff', borderRadius: 10, padding: 18, width: 920, maxWidth: '95vw', maxHeight: '95vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' },
  modalHead: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 },
  row3:      { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 10 },
  label:     { display: 'block', fontSize: 12, color: '#64748b', marginBottom: 3 },
  input:     { width: '100%', padding: '0 10px', height: 38, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: '#fff' },
  tableInner:{ width: '100%', borderCollapse: 'collapse', marginTop: 4 },
  thInner:   { background: '#f8fafc', padding: '6px 8px', textAlign: 'left', fontSize: 11, color: '#475569', fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' },
  tdInner:   { padding: '4px 6px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  balance:   { textAlign: 'right', marginTop: 10, fontSize: 13, fontWeight: 600 },
};
